import {
  NO_DEFAULT_SELECTED_FILTER_VALUE,
  getDefaultSelectedValues,
  serializeDefaultSelectedValues,
} from "shared/containers/Filters/defaultSelectedFilters";
import isDirectOnly from "shared/containers/Filters/isDirectOnly";
import { SearchQuery } from "shared/data/reducers/types/storage";
import {
  Flights,
  OTransportCompanies,
  Company as TransportCompany,
} from "shared/gol-api/types/flightSearch";
import { isInt, isTrue, toArray } from "shared/lib/functions";

import * as constants from "./constants";
import { AirlinesFilterType, FlightType } from "./types";

const TRANSPORT_COMPANIES_FILTER_SCOPE = "transport_companies_filter";

export const hoursToDuration = (hours: number) => {
  if (hours % 1 !== 0) return `${Math.floor(hours)}h ${(hours % 1) * 60}m`;
  return `${hours}h`;
};

export function rangeNumToDuration(value: number) {
  return `${Math.floor(value)}h ${isInt(value) ? "" : "30m"}`;
}

export const getDefaultRanges = (
  searchQuery: SearchQuery,
  lowestPrice: number,
  highestPrice: number,
  // take mainly from query params (when page is loaded) -> automatically activate filter
  // otherwise take default values
  init = false
) => {
  const paramKeys = Object.keys(searchQuery).filter((param) =>
    param.includes("filter_")
  );

  const newObj = {
    timeFilters: {},
  };

  paramKeys.forEach((val) => {
    const index = Number(val.charAt(7));
    let filterType = "";
    if (val.includes("arrival")) {
      filterType = "arrival";
    } else if (val.includes("departure")) {
      filterType = "departure";
    } else if (val.includes("transfer")) {
      filterType = "transfer";
    }

    const isMax = val.includes("max")
      ? true
      : val.includes("min")
      ? false
      : null;

    newObj.timeFilters = {
      ...newObj.timeFilters,
      [index]:
        newObj.timeFilters[index] !== undefined
          ? {
              ...newObj.timeFilters[index],
              [filterType]: isMax
                ? [
                    ...(newObj.timeFilters[index][filterType] || []),
                    searchQuery[val],
                  ]
                : isMax !== null
                ? [
                    searchQuery[val],
                    ...(newObj.timeFilters[index][filterType] || []),
                  ]
                : searchQuery[val],
            }
          : {
              [filterType]: isMax
                ? [...[], searchQuery[val]]
                : isMax !== null
                ? [searchQuery[val], ...[]]
                : searchQuery[val],
            },
    };
  });

  let price = [lowestPrice, highestPrice];

  if (
    searchQuery.lowestPrice !== undefined &&
    searchQuery.highestPrice !== undefined &&
    init
  ) {
    price = [Number(searchQuery.lowestPrice), Number(searchQuery.highestPrice)];
  }

  return {
    timeFilters: { ...newObj.timeFilters },
    priceFilter: [...price],
  };
};

export const getCleanTransferFilters = (
  query: SearchQuery,
  airportsTransfers: string[][]
) => {
  const flightType = getFlightType(query);
  const numberOfFlights =
    flightType === "ONE_WAY"
      ? 1
      : flightType === "RETURN"
      ? 2
      : getNumberOfFlights(query);

  let obj = {};

  if (isDirectOnly(query.max_transfers)) {
    return {};
  }

  for (let i = 0; i < numberOfFlights; i++) {
    const transferFilterName = `airports_${i}_transfers`;

    obj = {
      ...obj,
      ...(airportsTransfers[i]?.length &&
      query[transferFilterName] === undefined
        ? { [transferFilterName]: airportsTransfers[i]?.join("-") }
        : {}),
    };
  }

  return obj;
};

export const getFlightType = (query: SearchQuery): FlightType => {
  let flightType: FlightType = "MULTI";

  if (query.departureDate !== undefined && query.returnDate === "") {
    flightType = "ONE_WAY";
  }

  if (query.departureDate !== undefined && query.returnDate !== "") {
    flightType = "RETURN";
  }

  return flightType;
};

export const getNumberOfFlights = (query) => {
  const flightType = getFlightType(query);

  if (flightType === "ONE_WAY") {
    return 1;
  }

  if (flightType === "RETURN") {
    return 2;
  }

  return (
    Object.keys(query).filter((param) => param.startsWith("flight_")).length / 3
  );
};

export const getUsedFilterNames = (
  query: SearchQuery,
  airportsTransfers: string[][],
  activeFilter: number
) => {
  const queryCopy = { ...query };

  const allTransferFilters: string[] = airportsTransfers[activeFilter];
  const transfersFilterName = `airports_${activeFilter}_transfers`;
  const transfersFilterFromQuery = queryCopy[transfersFilterName];
  const transfersFilterValues: string[] = transfersFilterFromQuery?.split("-");
  const transfersFilterCompared =
    transfersFilterFromQuery !== NO_DEFAULT_SELECTED_FILTER_VALUE &&
    allTransferFilters?.length === transfersFilterValues?.length;

  return Object.keys(queryCopy).filter((key) => {
    if (key === transfersFilterName) {
      return !transfersFilterCompared;
    }

    return (
      key.includes("filter") ||
      constants.FILTERS_NAMES.includes(key) ||
      (key.includes("airports") && !key.includes("transfers"))
    );
  });
};

export const getMaxNumTransfers = (
  query: SearchQuery,
  maxNumTransfers: number
) => {
  return (
    Number(
      query.max_transfers !== undefined ? query.max_transfers : maxNumTransfers
    ) || 0
  );
};

export const splitQueryParam = (query: SearchQuery, key: string) => {
  return toArray(query[key] !== undefined ? String(query[key]).split("-") : []);
};

export const getDefaultSelectedCheckboxValues = (
  query: SearchQuery,
  key: string,
  allValues: string[]
): string[] => getDefaultSelectedValues(query[key], allValues);

export const getDefaultSelectedCheckboxFilterValue = (
  query: SearchQuery,
  key: string,
  code: string,
  allValues: string[]
) => {
  const selectedValues = getDefaultSelectedCheckboxValues(
    query,
    key,
    allValues
  );
  const selectedValueSet = new Set(selectedValues);

  if (selectedValueSet.has(code)) {
    selectedValueSet.delete(code);
  } else {
    selectedValueSet.add(code);
  }

  const serializedValues = serializeDefaultSelectedValues(
    Array.from(selectedValueSet),
    allValues
  );
  const newQuery = { ...query };
  delete newQuery[key];

  if (serializedValues !== undefined) {
    newQuery[key] = serializedValues;
  }

  return newQuery;
};

export const getOnlyCheckboxFilterValue = (
  query: SearchQuery,
  key: string,
  code: string
) => ({
  ...query,
  [key]: code,
});

export const getAirlinesFilterType = (
  query: SearchQuery
): AirlinesFilterType | false => {
  if (isTrue(query.onlySelectedNoCombinations)) {
    return constants.ONLY_SELECTED_NO_COMBINATIONS;
  }

  if (isTrue(query.onlySelected)) {
    return constants.ONLY_SELECTED;
  }

  return false;
};

export const getAirlineOptionFilterValue = (
  query: SearchQuery,
  type: AirlinesFilterType,
  activeFilter: number
): SearchQuery => {
  const activeAirlinesFilter = getAirlinesFilterType(query);
  const newQuery = { ...query };

  delete newQuery.onlySelected;
  delete newQuery.onlySelectedNoCombinations;

  if (activeAirlinesFilter !== type) {
    newQuery[type] = "true";
    newQuery[TRANSPORT_COMPANIES_FILTER_SCOPE] = String(activeFilter);
  } else if (newQuery.transport_companies === undefined) {
    delete newQuery[TRANSPORT_COMPANIES_FILTER_SCOPE];
  }

  return newQuery;
};

export const getNewCheckboxFilterValue = (
  query: SearchQuery,
  key: string,
  code: string
) => {
  let params = splitQueryParam(query, key);
  let wasDeleting = false;

  params = params.filter((param) => {
    if (param === code) wasDeleting = true;
    return param !== code;
  });

  if (!wasDeleting) {
    params.push(code);
  }

  const newQuery = { ...query };

  delete newQuery[key];

  if (params.length > 0) {
    params = params.length === 1 ? params[0] : params.join("-");
    newQuery[key] = params;
  }

  return newQuery;
};

export const getTransportCompaniesForFilter = (
  flightOffers: Flights,
  transportCompanies: OTransportCompanies[],
  activeFilter = 0
) => {
  if (flightOffers.length === 0) {
    return;
  }

  const oTransportCompanies: OTransportCompanies = {};

  flightOffers.forEach((flightOfferInner) => {
    flightOfferInner.FlightItinerary.FlightStream[activeFilter]?.forEach(
      (flightStream) => {
        flightStream.FlightSegments.FlightSegment.forEach((flightSegment) => {
          const transportCompany: TransportCompany =
            transportCompanies[flightSegment.MarketingAirline];

          if (transportCompany !== undefined) {
            oTransportCompanies[transportCompany.Code] = {
              ...transportCompany,
            };
          }
        });
      }
    );
  });

  return Object.values(oTransportCompanies).sort((a, b) => {
    if (a.Name.$t > b.Name.$t) return 1;
    if (a.Name.$t < b.Name.$t) return -1;
    return 0;
  });
};

export const parseStarsQueryParam = (starsQuery: string): string[] => {
  if (!starsQuery) {
    return [];
  }
  return starsQuery.split(",").flatMap((part: string) => {
    if (part.includes("-")) {
      return part.split("-");
    }
    return part;
  });
};

export const parseMealOptionsQueryParam = (
  mealOptionsQuery: string
): string[] => {
  if (!mealOptionsQuery) {
    return [];
  }
  return mealOptionsQuery.split("-");
};
